import React, { useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Highlighter from 'react-highlight-words';
import { Link, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Globe, UserRound, Users } from 'lucide-react';
import { RootState } from '../../../../configuration';
import Nametag from '../../../../components/cosmetics/nametag.component';
import { buildProfileSlug } from '../../../../utils/profileSlug';
import { IPostModel } from '../../../../models';
import { handleFormatHastag } from '../../../../utils/formatHastag';
import PostTypeImage from '../../../../components/post/post-types/post-type-image';
import PostTypeVideo from '../../../../components/post/post-types/post-type-video';
import PostTypeShare from '../../../../components/post/post-types/post-type-share';
import { TriangleAlert } from 'lucide-react';
import { AdminButton, AdminModCard } from '../../ui';

dayjs.extend(utc);

// Dựng phần hiển thị bài viết theo thẻ kiểm duyệt của thiết kế mới (thay cho thẻ bài của người dùng).
// Dữ liệu hiển thị giữ như component Post khi isAdmin: người đăng, thời gian, nội dung (tô từ cấm),
// hashtag, bài chia sẻ, ảnh/video, che nội dung nhạy cảm 18+.

const AvatarImg = ({ post }: { post?: IPostModel | any }) => (
  <img
    className="adm-mod-card__avatar"
    src={post?.userAvatar || '/default-avatar.svg'}
    alt={post?.userFullName || post?.username || 'Avatar'}
  />
);

export const postDisplayName = (post?: IPostModel | any) =>
  post?.userFullName || post?.username || 'Người dùng không xác định';

/** Avatar + tên (liên kết sang hồ sơ tác giả, khung tên nếu có) + dòng phụ, như PostHeader cũ. */
export const usePostAuthor = (post?: IPostModel | any) => {
  const profile = useSelector((state: RootState) => state.features.auth.profile);
  const isMe = !!profile?.userId && profile.userId === post?.userId;
  const to = isMe
    ? '/profile'
    : `/user/${buildProfileSlug(post?.userFullName, post?.userSequenceId, post?.username)}`;
  const audience = post?.sharedPostAudienceType;
  const isShare = post?.postType === 'share';
  return {
    avatar: (
      <Link to={to} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
        <AvatarImg post={post} />
      </Link>
    ),
    name: (
      <>
        <Nametag userId={post?.userId} banner compact>
          <Link to={to} target="_blank" rel="noreferrer">
            {postDisplayName(post)}
          </Link>
        </Nametag>
        {isShare && <span style={{ fontWeight: 400, marginLeft: 4 }}>đã chia sẻ 1 bài viết</span>}
      </>
    ),
    shareIcon: isShare ? (
      <span style={{ marginLeft: 6, display: 'inline-flex', verticalAlign: 'middle' }}>
        {audience === 0 && <Globe size={14} />}
        {audience === 2 && <Users size={14} />}
        {audience === 1 && <UserRound size={14} />}
      </span>
    ) : null,
  };
};

/** Đầu thẻ bài dùng trong khung riêng (trang report). */
export const PostHead = ({ post }: { post?: IPostModel | any }) => {
  const author = usePostAuthor(post);
  return (
    <div className="adm-mod-card__head">
      {author.avatar}
      <div className="adm-mod-card__who">
        <div className="adm-mod-card__name">{author.name}</div>
        <div className="adm-mod-card__meta">
          {postTimeAgo(post)}
          {author.shareIcon}
        </div>
      </div>
    </div>
  );
};

export const postTimeAgo = (post?: IPostModel | any) =>
  post?.createdAt ? dayjs.utc(post.createdAt).fromNow() : '';

export const PostHashtags = ({ post }: { post?: IPostModel | any }) => {
  if (!post?.hashtags || post.hashtags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-2">
      {post.hashtags
        .split(' ')
        .filter(Boolean)
        .map((ht: string, index: number) => (
          <NavLink
            key={index}
            to={`/hashtag/${handleFormatHastag(ht)}`}
            className={({ isActive }) => `adm-hashtag ${isActive ? 'pointer-events-none' : ''}`}
          >
            #{handleFormatHastag(ht)}
          </NavLink>
        ))}
    </div>
  );
};

export const hasPostMedia = (post?: IPostModel | any) =>
  !!(
    (post?.postType === 'video' && post?.postVideoResponses?.length) ||
    ((post?.postType === 'image' || post?.postType === 'normal') && post?.postImageResponses?.length)
  );

export const PostMedia = ({ post }: { post?: IPostModel | any }) => {
  if (!post) return null;
  if (post.postType === 'video') return <PostTypeVideo post={post} />;
  if (post.postType === 'image' || post.postType === 'normal') return <PostTypeImage post={post} />;
  return null;
};

/** Nội dung chữ + hashtag + bài chia sẻ (đi vào thân thẻ). */
export const PostText = ({ post, highlightWords = [] }: { post?: IPostModel | any; highlightWords?: string[] }) => (
  <>
    {post?.content ? (
      <Highlighter
        highlightClassName="bg-yellow-300"
        searchWords={highlightWords}
        textToHighlight={post.content}
      />
    ) : null}
    <PostHashtags post={post} />
    {post?.postType === 'share' && <PostTypeShare post={post?.sharedPost} />}
  </>
);

const SensitiveNotice = ({ onReveal }: { onReveal: () => void }) => (
  <div className="adm-strip adm-strip--warning" role="status">
    <TriangleAlert size={20} className="adm-strip__icon" />
    <span style={{ flex: 1 }}>Nội dung nhạy cảm (18+). Nội dung có thể không phù hợp với tất cả mọi người.</span>
    <AdminButton size={32} variant="outline" onClick={onReveal}>
      Xem nội dung
    </AdminButton>
  </div>
);

/** Che nội dung nhạy cảm 18+ (làm mờ, bấm "Xem nội dung" mới hiện), dùng chung cho mọi thẻ bài. */
export const useSensitiveCover = (post?: IPostModel | any) => {
  const [revealed, setRevealed] = useState(false);
  const covered = !!post?.isSensitive && !revealed;
  return {
    blur: covered ? 'pointer-events-none select-none blur-xl' : '',
    notice: covered ? <SensitiveNotice onReveal={() => setRevealed(true)} /> : null,
  };
};

interface AdminPostModCardProps {
  post: IPostModel | any;
  actions?: React.ReactNode;
  headExtra?: React.ReactNode;
  highlightWords?: string[];
  /** thêm dưới nội dung bài (từ vi phạm, lý do từ chối ...) */
  extra?: React.ReactNode;
  className?: string;
}

export const AdminPostModCard = ({ post, actions, headExtra, highlightWords, extra, className }: AdminPostModCardProps) => {
  const { blur, notice } = useSensitiveCover(post);
  const author = usePostAuthor(post);
  return (
    <AdminModCard
      className={className}
      avatar={author.avatar}
      name={author.name}
      meta={
        <>
          {postTimeAgo(post)}
          {author.shareIcon}
        </>
      }
      headExtra={headExtra}
      media={
        hasPostMedia(post) ? (
          <div className={blur}>
            <PostMedia post={post} />
          </div>
        ) : undefined
      }
      actions={actions}
    >
      {notice}
      <div className={`flex flex-col gap-2 ${blur}`}>
        <PostText post={post} highlightWords={highlightWords} />
      </div>
      {extra}
    </AdminModCard>
  );
};
